import { axiosTassoQuintoApiInstance } from '../tasso-quinto';

/**
 * Limits the policy declares for one Advancement data field. They exist to give
 * the operator immediate feedback: the server revalidates the same rules and
 * stays authoritative.
 */
export interface ManagedLeadAdvancementConstraints {
  /** Plain text: maximum length of the trimmed value. */
  maxLength?: number;
  /** Currency: the amount has to be strictly greater than this. */
  greaterThan?: number;
  /** Currency: how many decimal places the amount may carry. */
  maxDecimals?: number;
}

/**
 * One Advancement data field the selected stage asks for, in the order the
 * form has to render it. `key` and `kind` are semantic and product-agnostic:
 * the backoffice never learns the HubSpot property behind them, and both are
 * read as open strings because the server owns those vocabularies. A `kind`
 * this release cannot render fails closed instead of being dropped.
 */
export interface ManagedLeadAdvancementField {
  key: string;
  label: string;
  kind: string;
  required: boolean;
  constraints?: ManagedLeadAdvancementConstraints;
}

export interface ManagedLeadStageRequirements {
  /**
   * `required` when an update from this stage must carry a Nota, else
   * `not_applicable`. Left open: the server owns this vocabulary and may grow
   * it without a frontend release.
   */
  note: string;
  /**
   * Drop availability is decided here and not by `updatable`: the read-only
   * in-progress stages keep `dropAllowed: true` while `updatable` is `false`.
   */
  dropAllowed: boolean;
  /**
   * Advancement data this stage requires, already ordered. Absent where the
   * stage asks for none: the backoffice keeps no stage-to-field matrix of its
   * own and renders exactly what arrives here.
   */
  fields?: ManagedLeadAdvancementField[];
}

/**
 * One selectable target of the update form. It describes the destination, not
 * the stage the lead sits in: selectability, successors and Drop availability
 * belong to the current stage and are deliberately absent here, and its own
 * `requirements.note` says whether reaching this target needs a Nota.
 */
export interface ManagedLeadTransition {
  id: string;
  key: string;
  slug: string;
  label: string;
  category: 'in_progress' | 'won' | 'dropped';
  requirements: { note: string; fields?: ManagedLeadAdvancementField[] };
}

export interface ManagedLeadStage {
  id: string;
  key: string;
  slug: string;
  label: string;
  category: 'in_progress' | 'won' | 'dropped';
  selectable: boolean;
  successors: string[];
  requirements: ManagedLeadStageRequirements;
  updatable: boolean;
  alert?: string;
}

/**
 * One of the five approved Drop reasons. The list is policy, not a frontend
 * constant: the detail publishes it in `dropReasons` and the mutation accepts
 * nothing else.
 */
export interface ManagedLeadDropReason {
  key: string;
  label: string;
}

/**
 * A Drop request awaiting administrative review. It never appears before the
 * backend has turned it `pending`, so a request still being created is
 * invisible here — and `cancellable` is always `false`: only the admin review
 * closes a pending request.
 */
export interface ManagedLeadDropRequest {
  id: string;
  status: string;
  reason: ManagedLeadDropReason | null;
  /** Stage the lead sat on when the Drop was asked for; it may have moved since. */
  stageAtRequest: string;
  requestedAt: string | null;
  cancellable: boolean;
}

export interface ManagedLead {
  id: string;
  stage: ManagedLeadStage;
  dealName: string | null;
  phone: string | null;
  lastModifiedAt: string | null;
  amount: number | string | null;
  createdAt: string | null;
  needsUpdate?: boolean;
  /**
   * Integer age of the last commercial activity, calculated by Tasso Quinto.
   * Optional while the additive backend field is being deployed.
   */
  daysSinceLastActivity?: number | null;
  /**
   * A pending Drop is a badge, not a category: the lead stays listed under the
   * category its stage decides until the review approves the request.
   */
  pendingDrop: ManagedLeadDropRequest | null;
}

export interface ManagedLoanLead extends ManagedLead {
  purpose: string | boolean | null;
}

export interface ManagedCdqLead extends ManagedLead {
  occupation: string | boolean | null;
}

export interface ManagedLeadListResponse<T> {
  results: T[];
  page: number;
  limit: number;
  totalPages: number | null;
  totalResults: number | null;
  resultLimitReached: boolean;
}

export type ManagedLeadCategory = 'in_progress' | 'won' | 'dropped';

export interface ManagedLeadListParams {
  category: ManagedLeadCategory;
  page: number;
  limit: 25;
  search?: string;
  stages?: string;
  needsUpdate?: true;
}

export interface ManagedLeadLifecycle {
  version: number;
  product: 'prestito' | 'cdq';
  categories: Record<ManagedLeadCategory, string[]>;
  stages: ManagedLeadStage[];
}

export type ManagedLeadField<T> =
  | { state: 'value'; value: T }
  | { state: 'missing' }
  | { state: 'not_applicable' }
  | { state: 'unknown'; value?: string | number | boolean }
  | {
      state: 'unknown_applicability';
      value?: string | number | boolean;
    };

export interface ManagedLeadOwner {
  id: string;
  name: string | null;
  email: string | null;
}

/** Summary fields every product detail exposes, whatever the product is. */
export interface ManagedLeadDetailCommonFields {
  dealName: ManagedLeadField<string>;
  phone: ManagedLeadField<string>;
  email: ManagedLeadField<string>;
  salesManager: ManagedLeadField<ManagedLeadOwner>;
  createdAt: ManagedLeadField<string>;
  lastModifiedAt: ManagedLeadField<string>;
  amount: ManagedLeadField<number | string>;
  income: ManagedLeadField<number | string>;
}

/**
 * Server-driven update contract carried by every product detail: the backoffice
 * builds the update form out of these metadata instead of restating the
 * lifecycle rules, and the backend revalidates them on submit anyway.
 */
export interface ManagedLeadUpdateContract {
  /** Whether the current stage accepts a stage or Nota update at all. */
  updatable: boolean;
  /** Current stage first, then the successors the policy declares selectable. */
  transitions: ManagedLeadTransition[];
  /**
   * Requirements of the stage the lead sits in — `note` is what an update that
   * keeps the current stage needs. Not to be confused with the requirements a
   * `transitions` entry states about its own destination.
   */
  requirements: ManagedLeadStageRequirements;
  /**
   * The reasons a Drop request may carry from this stage, empty where Drop is
   * not on offer. Availability is still `requirements.dropAllowed`: a stage can
   * be read-only and drop-able at the same time.
   */
  dropReasons: ManagedLeadDropReason[];
  /** The request already awaiting review on this lead, or `null`. */
  pendingDrop: ManagedLeadDropRequest | null;
  /**
   * Canonical value HubSpot currently holds for every supported semantic key,
   * whatever stage is selected. It is the only source the form initializes and
   * resets Advancement data from: what the operator typed and discarded is
   * never restored, and a key HubSpot has no value for arrives `missing` and
   * becomes mandatory as soon as a stage asks for it.
   */
  advancementData: Record<string, ManagedLeadField<string>>;
}

export interface ManagedLeadDetail extends ManagedLeadUpdateContract {
  id: string;
  stage: ManagedLeadStage;
  fields: ManagedLeadDetailCommonFields;
}

export interface ManagedLoanLeadDetail extends ManagedLeadDetail {
  fields: ManagedLeadDetailCommonFields & {
    purpose: ManagedLeadField<string | boolean>;
    employmentContract: ManagedLeadField<string>;
    previousFinancing: ManagedLeadField<boolean>;
    latePayments: ManagedLeadField<boolean>;
    companySeniority: ManagedLeadField<string | number>;
  };
}

export interface ManagedCdqLeadDetail extends ManagedLeadDetail {
  fields: ManagedLeadDetailCommonFields & {
    occupation: ManagedLeadField<string>;
    age: ManagedLeadField<number | string>;
    spid: ManagedLeadField<boolean>;
    citizenship: ManagedLeadField<boolean>;
    existingCommitments: ManagedLeadField<boolean>;
    latePayments: ManagedLeadField<boolean>;
    employeesNumber: ManagedLeadField<number | string>;
    companySeniority: ManagedLeadField<number | string>;
    monthlyIncomeFrequency: ManagedLeadField<string>;
  };
}

export interface ManagedLeadNote {
  id: string;
  text: string;
  createdAt: string | null;
  author: ManagedLeadField<ManagedLeadOwner>;
}

export interface ManagedLeadNotesResponse {
  results: ManagedLeadNote[];
  page: number;
  limit: number;
  totalPages: number;
  totalResults: number;
}

export type ManagedLeadProductPath = 'prestiti' | 'cessioni-del-quinto';

export interface ManagedLeadDocumentArchive {
  blob: Blob;
  filename: string;
}

export interface ManagedLeadUpdatePayload {
  /** Stage the operator was looking at; a concurrent change answers 409. */
  expectedCurrentStage: string;
  targetStage: string;
  /**
   * Already trimmed: the server validates the trimmed text, up to 5.000 chars.
   * Absent where the destination declares `note: 'not_applicable'` — sending it
   * there is an `invalid_request`, exactly as omitting it where it is required.
   */
  note?: string;
  /**
   * Canonical Advancement data, keyed by semantic name and holding exactly the
   * fields the target stage declares. Absent where it declares none: a field
   * the form did not show is never sent, so an update can only write what the
   * operator was actually looking at and never clears unrelated HubSpot data.
   */
  fields?: Record<string, string>;
}

/** Why the Nota did not land after the stage change already had. */
export interface ManagedLeadNoteFailure {
  code: string;
  retryable: boolean;
}

export interface ManagedLeadUpdateResult {
  /**
   * `partial_success` only ever follows an applied stage change: the Nota is
   * the single outstanding step, and the same key resumes it.
   */
  outcome: 'success' | 'partial_success';
  currentStage: string;
  targetStage: string;
  stageUpdated: boolean;
  /**
   * `true` when the Deal write changed at least one Advancement data property.
   * A same-stage update that resubmits unchanged values reports `false` here
   * and leaves the Nota as the only thing that happened.
   */
  fieldsUpdated: boolean;
  note: {
    created: boolean;
    id: string | null;
    failure?: ManagedLeadNoteFailure;
  };
  /** `true` when the key replayed an update that had already succeeded. */
  replayed: boolean;
}

export const getManagedLeadLifecycle = async (
  product: ManagedLeadProductPath
): Promise<ManagedLeadLifecycle> => {
  const response = await axiosTassoQuintoApiInstance.get<ManagedLeadLifecycle>(
    `/v1/backoffice/${product}/lifecycle`
  );

  return response.data;
};

export const listManagedLoanLeads = async (
  params: ManagedLeadListParams
): Promise<ManagedLeadListResponse<ManagedLoanLead>> => {
  const response = await axiosTassoQuintoApiInstance.get<
    ManagedLeadListResponse<ManagedLoanLead>
  >('/v1/backoffice/prestiti/deals', { params });

  return response.data;
};

export const listManagedCdqLeads = async (
  params: ManagedLeadListParams
): Promise<ManagedLeadListResponse<ManagedCdqLead>> => {
  const response = await axiosTassoQuintoApiInstance.get<
    ManagedLeadListResponse<ManagedCdqLead>
  >('/v1/backoffice/cessioni-del-quinto/deals', { params });

  return response.data;
};

export const getManagedLoanLead = async (
  dealId: string
): Promise<ManagedLoanLeadDetail> => {
  const response = await axiosTassoQuintoApiInstance.get<ManagedLoanLeadDetail>(
    `/v1/backoffice/prestiti/deals/${encodeURIComponent(dealId)}`
  );

  return response.data;
};

export const listManagedLoanNotes = async (
  dealId: string,
  params: { page: number; limit: 25 }
): Promise<ManagedLeadNotesResponse> => {
  const response =
    await axiosTassoQuintoApiInstance.get<ManagedLeadNotesResponse>(
      `/v1/backoffice/prestiti/deals/${encodeURIComponent(dealId)}/notes`,
      { params }
    );

  return response.data;
};

export const getManagedCdqLead = async (
  dealId: string
): Promise<ManagedCdqLeadDetail> => {
  const response = await axiosTassoQuintoApiInstance.get<ManagedCdqLeadDetail>(
    `/v1/backoffice/cessioni-del-quinto/deals/${encodeURIComponent(dealId)}`
  );

  return response.data;
};

export const listManagedCdqNotes = async (
  dealId: string,
  params: { page: number; limit: 25 }
): Promise<ManagedLeadNotesResponse> => {
  const response =
    await axiosTassoQuintoApiInstance.get<ManagedLeadNotesResponse>(
      `/v1/backoffice/cessioni-del-quinto/deals/${encodeURIComponent(
        dealId
      )}/notes`,
      { params }
    );

  return response.data;
};

const archiveFilename = (contentDisposition?: string): string => {
  const encodedFilename = contentDisposition?.match(
    /filename\*=UTF-8''([^;]+)/i
  )?.[1];
  if (encodedFilename) {
    try {
      return decodeURIComponent(encodedFilename);
    } catch {
      // Fall through to the plain filename or the stable default.
    }
  }

  return (
    contentDisposition?.match(/filename="?([^";]+)"?/i)?.[1] || 'documenti.zip'
  );
};

/**
 * Downloads the live HubSpot documents of one authorized Deal. The server uses
 * the Deal id to enforce product and ownership scope before reading its files.
 */
export const getManagedLeadDocumentArchive = async (
  product: ManagedLeadProductPath,
  dealId: string
): Promise<ManagedLeadDocumentArchive> => {
  const response = await axiosTassoQuintoApiInstance.get<Blob>(
    `/v1/backoffice/${product}/deals/${encodeURIComponent(
      dealId
    )}/documents/archive`,
    { responseType: 'blob' }
  );

  return {
    blob: response.data,
    filename: archiveFilename(response.headers['content-disposition']),
  };
};

/**
 * Both products expose the same update route under their own namespace, so the
 * product path is the only thing that varies: keeping one function avoids two
 * copies of the idempotency header contract.
 */
export const updateManagedLead = async (
  product: ManagedLeadProductPath,
  dealId: string,
  payload: ManagedLeadUpdatePayload,
  idempotencyKey: string
): Promise<ManagedLeadUpdateResult> => {
  const response =
    await axiosTassoQuintoApiInstance.post<ManagedLeadUpdateResult>(
      `/v1/backoffice/${product}/deals/${encodeURIComponent(dealId)}/updates`,
      payload,
      { headers: { 'Idempotency-Key': idempotencyKey } }
    );

  return response.data;
};

/**
 * A Request values correction. `values` is symmetric with the set the product
 * declares correctable: every declared key is present, so the server can refuse
 * a key the operator was never shown instead of ignoring it, and a figure the
 * modal does not edit travels as HubSpot holds it. Each value is canonical -
 * an amount is a decimal string, never a float - and an empty string means the
 * figure is not stated, which the server accepts only where it holds nothing.
 */
export interface ManagedLeadRequestValuesPayload {
  values: Record<string, string>;
}

export interface ManagedLeadRequestValuesResult {
  /**
   * `partial_success` only ever follows a property write that landed: the
   * automatic Nota is the single outstanding step, and the same key resumes it.
   */
  outcome: 'success' | 'partial_success';
  /**
   * Semantic keys the correction actually wrote. It is empty when the submitted
   * figures matched the stored ones: nothing was corrected, so nothing was
   * written and no Nota was created.
   */
  valuesUpdated: string[];
  note: {
    created: boolean;
    id: string | null;
    failure?: ManagedLeadNoteFailure;
  };
  /** `true` when the key replayed a correction that had already succeeded. */
  replayed: boolean;
}

/**
 * Corrects the figures a customer stated at acquisition. It is a second write
 * path beside `updateManagedLead` and not a variant of it: it changes no stage,
 * asks for no Nota of the operator's, and the server records the change as an
 * automatic Nota of its own. The `Idempotency-Key` is carried the same way,
 * because both paths share one register on the server.
 */
export const correctManagedLeadRequestValues = async (
  product: ManagedLeadProductPath,
  dealId: string,
  payload: ManagedLeadRequestValuesPayload,
  idempotencyKey: string
): Promise<ManagedLeadRequestValuesResult> => {
  const response =
    await axiosTassoQuintoApiInstance.patch<ManagedLeadRequestValuesResult>(
      `/v1/backoffice/${product}/deals/${encodeURIComponent(
        dealId
      )}/request-values`,
      payload,
      { headers: { 'Idempotency-Key': idempotencyKey } }
    );

  return response.data;
};

export interface ManagedLeadDropRequestPayload {
  /** One of the keys the detail published in `dropReasons`. */
  reason: string;
  /** Already trimmed: not empty, up to 5.000 characters, no attachments. */
  note: string;
}

/**
 * Asks for the Drop of a managed lead. There is no `Idempotency-Key` here: the
 * server deduplicates by product and Deal, so a second request while one is
 * awaiting review is answered with the request already in flight rather than
 * writing a new one.
 */
export const requestManagedLeadDrop = async (
  product: ManagedLeadProductPath,
  dealId: string,
  payload: ManagedLeadDropRequestPayload
): Promise<ManagedLeadDropRequest> => {
  const response =
    await axiosTassoQuintoApiInstance.post<ManagedLeadDropRequest>(
      `/v1/backoffice/${product}/deals/${encodeURIComponent(
        dealId
      )}/drop-requests`,
      payload
    );

  return response.data;
};

/** The scheduler accepted the email; acceptance is not delivery. */
export interface ManagedLeadUpdateRequestResult {
  outcome: 'accepted';
}

export interface ManagedLeadsUpdateRequestResult
  extends ManagedLeadUpdateRequestResult {
  /** Leads listed in the email: the first 100 in the list order at most. */
  listed: number;
  /**
   * Leads the In gestione selection matched; `null` only past the HubSpot
   * search limit. `listed < found` means the email was truncated.
   */
  found: number | null;
}

/** The In gestione list filters the aggregate request reruns on the server. */
export interface ManagedLeadsUpdateRequestFilters {
  search?: string;
  stages?: string;
  needsUpdate?: true;
}

/**
 * Asks the owner of one In gestione lead to update it. Admin only: the server
 * requires a scope Tasso Mutuo grants to admins alone, also while impersonating.
 */
export const requestManagedLeadUpdate = async (
  product: ManagedLeadProductPath,
  dealId: string
): Promise<ManagedLeadUpdateRequestResult> => {
  const response =
    await axiosTassoQuintoApiInstance.post<ManagedLeadUpdateRequestResult>(
      `/v1/backoffice/${product}/deals/${encodeURIComponent(
        dealId
      )}/update-requests`
    );

  return response.data;
};

/**
 * Asks the effective owner to update the In gestione leads the filters select.
 * Only the filters travel: the server reruns the list search and decides which
 * leads, and whom to write to.
 */
export const requestManagedLeadsUpdate = async (
  product: ManagedLeadProductPath,
  filters: ManagedLeadsUpdateRequestFilters
): Promise<ManagedLeadsUpdateRequestResult> => {
  const response =
    await axiosTassoQuintoApiInstance.post<ManagedLeadsUpdateRequestResult>(
      `/v1/backoffice/${product}/update-requests`,
      filters
    );

  return response.data;
};
